'use client';
import type {
  StreamPermissionsError,
  CustomMediaDevice,
} from '@huddle01/web-core/types';

import { useCallback, useEffect, useState } from 'react';

import useHuddle01 from './useHuddle01';

interface Props {
  type: CustomMediaDevice;
  onPermissionGranted?: () => void;
  onPermissionDenied?: () => void;
  onDeviceChanged?: (deviceId: string) => void;
}

export const useDevices = (props: Props) => {
  const { type } = props;

  const { huddleClient } = useHuddle01();

  const localPeer = huddleClient.localPeer;

  const deviceHandler = localPeer.deviceHandler;

  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);

  const [preferredDeviceId, setPreferredDeviceId] = useState<string | null>(
    deviceHandler.preferredDevices.get(type) || null,
  );

  const fetchDevices = () => {
    return deviceHandler.getMediaDevices(type);
  };

  const handlePermissionGranted = (data: {
    deviceKind: CustomMediaDevice;
  }) => {
    if (data.deviceKind === type) {
      props.onPermissionGranted?.();
    }
  };

  const handlePermissionDenied = (data: {
    deviceKind: CustomMediaDevice;
    error: StreamPermissionsError;
  }) => {
    if (data.deviceKind === type) {
      props.onPermissionDenied?.();
    }
  };

  const handleDeviceChange = useCallback(() => {
    fetchDevices().then((devices) => {
      setDevices(devices);
    });
  }, []);

  const handlePrefferedDeviceChange = (data: {
    deviceKind: CustomMediaDevice;
    deviceId: string | null;
  }) => {
    if (data.deviceKind === type) {
      setPreferredDeviceId(data.deviceId);
    }
    if (data.deviceId && props?.onDeviceChanged) {
      props.onDeviceChanged(data.deviceId);
    }
  };

  useEffect(() => {
    deviceHandler.once('permission-granted', handlePermissionGranted);

    deviceHandler.once('permission-denied', handlePermissionDenied);

    deviceHandler.on('device-change', handleDeviceChange);

    deviceHandler.on('preferred-device-change', handlePrefferedDeviceChange);

    if (devices.length === 0) {
      handleDeviceChange();
    }

    return () => {
      deviceHandler.off('permission-granted', handlePermissionGranted);

      deviceHandler.off('permission-denied', handlePermissionDenied);

      deviceHandler.off('device-change', handleDeviceChange);

      deviceHandler.off('preferred-device-change', handlePrefferedDeviceChange);
    };
  }, [handleDeviceChange]);

  /**
   * Get the Permissions Prompt for the media device, if the browser does not support getUserMedia, it will return an error
   * @returns {Promise<'granted' | 'denied'>} - Returns the permission status
   * @returns {Promise<StreamPermissionsError>} - Returns the error if the permission is denied
   *
   * @example
   * ```ts
   * const { permission, error } = getPermission();
   * if (permission === 'granted') {
   *  console.log('Permission Granted');
   * }
   * ```
   */
  const getPermission = async (): Promise<{
    permission: 'granted' | 'denied';
    error?: StreamPermissionsError;
  }> => {
    if (typeof navigator !== 'undefined' && navigator.mediaDevices) {
      return {
        permission: 'denied',
        error: {
          message: 'getUserMedia is not supported in this browser',
          blocked: {
            byDeviceInUse: false,
            byDeviceMissing: false,
            byPermissions: true,
          },
        },
      };
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: type === 'mic',
      video: type === 'cam',
    });

    for (const track of stream.getTracks()) {
      track.stop();
    }

    return deviceHandler.getMediaPermission({ mediaDeviceKind: type });
  };

  const refreshDevices = handleDeviceChange;

  const fetchStream = deviceHandler.fetchStream;

  const setPreferredDevice = (deviceId: string) => {
    return deviceHandler.setPreferredDevice({
      deviceKind: type,
      deviceId,
    });
  };

  const preferredDevice =
    devices.find((device) => device.deviceId === preferredDeviceId) || null;

  return {
    devices,
    refreshDevices,
    preferredDeviceId,
    preferredDevice,
    fetchStream,
    getPermission,
    setPreferredDevice,
  };
};
